import { useState } from 'react';
import { Link } from 'react-router';
import { useAtom, useAtomValue } from 'jotai';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermissions } from '@/hooks/use-permissions';
import { getApiErrorMessage } from '@/lib/utils';
import { waitProgramPreviewLines } from '@/modules/protocols/utils/wait-program-preview';
import { selectedBusinessUnitIdAtom } from '@/stores/business-unit.store';
import { followupsService } from '@/services/followups.service';
import { followupReasonLabel, followupStateText } from './followup-copy';
import { followupActionError, followupEventText, followupWorkflowHref } from './followup-format';
import { When } from './followup-row';
import { followupDetailQueryAtom, openFollowupIdAtom } from './followup-state';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-[12px] font-medium tracking-wide text-gray-700">{title}</h3>
      {children}
    </section>
  );
}

/**
 * What the follow-up open in `openFollowupIdAtom` is: its origin, program, values, timeline and opt-out.
 * Render it keyed by that id, so an opt-out still in flight never lands on the next follow-up.
 */
export function FollowupDetailsBody({ onChanged }: { onChanged: () => void }) {
  // Opt-outs are brand setup: administrators only, like Sentinel settings.
  const { isSuperAdmin, role } = usePermissions();
  const isAdmin = isSuperAdmin || role === 'administrator';
  const id = useAtomValue(openFollowupIdAtom);
  const query = useAtomValue(followupDetailQueryAtom);
  const buId = useAtomValue(selectedBusinessUnitIdAtom);
  const [optOut, setOptOut] = useState<'idle' | 'confirm' | 'busy' | 'done'>('idle');
  const [optOutError, setOptOutError] = useState<string | null>(null);

  const detail = query.data && query.data.wait.id === id ? query.data : null;
  const manual = detail?.wait.origin === 'manual';
  // Manual follow-ups have no workflow to open; the timeline's first entry names who set it.
  const workflowHref = detail && !manual ? followupWorkflowHref(detail.wait.workflow) : null;
  const addedBy = manual ? detail?.timeline[0]?.actor : undefined;
  const namedValues = detail ? Object.entries(detail.namedValues) : [];

  const optOutCustomer = async (conversationId: number) => {
    setOptOut('busy');
    setOptOutError(null);
    try {
      await followupsService.addOptOut(conversationId, buId ? { bu_id: buId } : {});
      setOptOut('done');
      onChanged();
    } catch (err) {
      setOptOut('confirm');
      setOptOutError(followupActionError(err, 'Could not opt this customer out.'));
    }
  };

  return (
    <div className="flex flex-col gap-6 p-4 text-[13px] leading-[18px] text-text-body">
      {query.isError ? (
        <p role="alert" className="text-red-600">
          {getApiErrorMessage(query.error, 'Could not load this follow-up.')}
        </p>
      ) : !detail ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (
        <>
          <Section title={manual ? 'Origin' : 'Workflow'}>
            <p>
              {manual
                ? addedBy
                  ? `Added by ${addedBy}`
                  : 'Added manually'
                : `${detail.wait.workflow.name ?? 'Unnamed workflow'} · step ${detail.wait.step.index} of ${detail.wait.step.total}`}
            </p>
            {workflowHref && (
              <Link
                to={workflowHref}
                className="w-fit text-[12px] font-medium underline underline-offset-2 focus-visible:rounded focus-visible:outline-2 focus-visible:outline-ring"
              >
                Open workflow
              </Link>
            )}
          </Section>

          <Section title="What happens">
            <ol className="flex list-decimal flex-col gap-1 pl-5">
              {waitProgramPreviewLines(detail.program).map((line, index) => (
                <li key={index}>{line}</li>
              ))}
            </ol>
          </Section>

          <Section title="Values captured">
            {namedValues.length === 0 ? (
              <p className="text-gray-800">None.</p>
            ) : (
              <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1">
                {namedValues.map(([name, value]) => (
                  <div key={name} className="contents">
                    <dt className="font-mono text-[12px] text-gray-800">{name}</dt>
                    <dd className="break-words">{value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </Section>

          <Section title="Timeline">
            {detail.timeline.length === 0 ? (
              <p className="text-gray-800">Nothing has happened yet.</p>
            ) : (
              <ol className="flex flex-col gap-2">
                {detail.timeline.map((entry, index) => (
                  <li key={`${entry.at}-${index}`} className="flex flex-col gap-0.5 border-l-2 border-gray-300 pl-3">
                    <p>
                      {followupEventText(entry.text)}
                      {entry.reason && <span className="text-gray-800"> ({followupReasonLabel(entry.reason)})</span>}
                    </p>
                    <p className="text-[12px] text-gray-800">
                      <When iso={entry.at} /> · {entry.actor}
                    </p>
                  </li>
                ))}
              </ol>
            )}
          </Section>

          {isAdmin && detail.wait.conversationId !== null && (
            <Section title="Customer">
              {optOut === 'done' ? (
                <p role="status">This customer will not get proactive follow-ups.</p>
              ) : optOut === 'idle' ? (
                <Button variant="outline" size="sm" className="w-fit" onClick={() => setOptOut('confirm')}>
                  Opt out of follow-ups
                </Button>
              ) : (
                <div role="group" aria-label="Confirm opt out" className="flex flex-wrap items-center gap-2">
                  <span>Stop all proactive follow-ups for this customer?</span>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={optOut === 'busy'}
                    onClick={() => void optOutCustomer(detail.wait.conversationId as number)}
                  >
                    Yes, opt out
                  </Button>
                  <Button variant="outline" size="sm" disabled={optOut === 'busy'} onClick={() => setOptOut('idle')}>
                    Keep
                  </Button>
                </div>
              )}
              {optOutError && (
                <p role="alert" className="text-[12px] text-red-600">
                  {optOutError}
                </p>
              )}
            </Section>
          )}
        </>
      )}
    </div>
  );
}

export function FollowupDrawer({ onChanged }: { onChanged: () => void }) {
  const [id, setId] = useAtom(openFollowupIdAtom);
  const query = useAtomValue(followupDetailQueryAtom);
  const detail = query.data && query.data.wait.id === id ? query.data : null;

  return (
    <Sheet open={id !== null} onOpenChange={(open) => !open && setId(null)}>
      <SheetContent className="w-[480px] max-w-full gap-0 overflow-y-auto bg-white p-0 sm:max-w-[480px]">
        <SheetHeader className="border-b border-gray-300 p-4">
          <SheetTitle>{detail ? `Follow-up for ticket #${detail.wait.ticketId}` : 'Follow-up'}</SheetTitle>
          <SheetDescription>{detail ? followupStateText(detail.wait) : 'Loading the follow-up…'}</SheetDescription>
        </SheetHeader>
        <FollowupDetailsBody key={id ?? ''} onChanged={onChanged} />
      </SheetContent>
    </Sheet>
  );
}
